import { Controller } from "stimulus"

/**
 * Confirms a multi-file document upload before it starts, on records where
 * uploading looks like it might create a new version of a document that is
 * already there.
 *
 * The listener is bound to the form element rather than declared with
 * data-action on a child, and a cancel calls stopPropagation as well as
 * preventDefault. Both of those are deliberate:
 *
 *   - ActiveStorage's UJS registers a submit listener on `document` in the
 *     bubble phase (@rails/activestorage/src/ujs.js, start()) and, when the
 *     form holds a direct_upload input with files, calls preventDefault,
 *     uploads the blobs and re-clicks the submit button. A listener on the
 *     form runs before any listener on document, so this is the only place
 *     the question can be asked before the files leave the browser.
 *   - That handler never checks defaultPrevented, so preventDefault alone
 *     would not stop it -- the blobs would upload anyway and a cancel would
 *     leave them orphaned in storage. stopPropagation is what actually holds
 *     it back.
 *
 * data-turbo-confirm, the house convention elsewhere, cannot do this job:
 * Turbo's own submit handler is guarded by `if (!event.defaultPrevented)`
 * (turbo.es2017-esm.js, FormSubmitObserver#submitBubbled), so ActiveStorage's
 * preventDefault means Turbo sits out the first submit entirely and the
 * confirm would not be evaluated until the synthetic second one -- after the
 * upload it was meant to gate.
 */
export default class extends Controller {
  static get targets() {
    return ["input"]
  }

  connect() {
    this.form = this.element.closest("form")
    if (!this.form) return

    this.confirmMultiple = this.confirmMultiple.bind(this)
    this.form.addEventListener("submit", this.confirmMultiple)
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("submit", this.confirmMultiple)
    }
  }

  confirmMultiple(event) {
    if (this.selectedCount < 2) return
    if (this.alreadyUploaded) return
    if (window.confirm(this.message)) return

    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Whether these files have already been uploaded, meaning this submit is
   * ActiveStorage's own and the question has been asked and answered.
   *
   * Accepting the confirm does not submit the form: ActiveStorage prevents
   * that first submit, disables the file inputs, uploads the blobs and then
   * clicks the submit button again (ujs.js, handleFormSubmissionEvent and
   * submitForm). Disabling an input does not clear its FileList, so the same
   * files are still counted on that second pass and the staffer would be
   * asked twice. Cancelling the second question is the harmful one -- the
   * blobs are in storage by then, so it stops only the record update and
   * strands them.
   *
   * The disabled flag is the signal rather than an "already confirmed" instance
   * variable because it resets itself correctly: ActiveStorage re-enables the
   * inputs when an upload fails (and does not resubmit), so a hand-retry
   * after a failure is a fresh upload and gets asked again.
   */
  get alreadyUploaded() {
    return this.hasInputTarget && this.inputTarget.disabled
  }

  get selectedCount() {
    if (!this.hasInputTarget) return 0
    if (!this.inputTarget.files) return 0

    return this.inputTarget.files.length
  }

  get message() {
    return [
      `${this.selectedCount} files selected.`,
      "Each will be added as a new document, not as a new version of an existing one.",
      'To add a version of a document you already have, use "Upload New Version" on that document\'s card.'
    ].join(" ")
  }
}
